/**
 * AuraEffect - Visual aura effect around player
 * Shows glowing circle/ring effect based on element
 */

import { Renderer } from '@client/core/Renderer';
import { Camera } from '@client/core/Camera';
import { ElementType, Vector2 } from '@shared/types';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';

export interface AuraEffectConfig {
  skill: CustomSkillV2;
  position: Vector2;
}

/** Offscreen canvas cache for aura ring glow (outer ring gradient) */
interface AuraGlowCache {
  canvas: HTMLCanvasElement;
  zoom: number;
  radius: number;
}

/** Offscreen canvas cache for inner pulse gradient */
interface AuraInnerCache {
  canvas: HTMLCanvasElement;
  zoom: number;
}

/** Offscreen canvas cache for particle glow */
interface AuraParticleCache {
  canvas: HTMLCanvasElement;
  zoom: number;
}

export class AuraEffect {
  private skill: CustomSkillV2;
  private element: ElementType;
  public readonly position: Vector2;
  private radius: number;
  private color: string;
  private brightColor: string;
  private pulseTime: number = 0;
  private particleAngle: number = 0;
  public isComplete: boolean = false;

  // Toggle animation state (0=off, 1=fully on)
  private toggleProgress: number = 0;
  private toggleSpeed: number = 3.0; // Full transition in ~0.33s

  // Cached offscreen canvases (per-element, shared across instances)
  private static glowCache = new Map<string, AuraGlowCache>();
  private static innerCache = new Map<string, AuraInnerCache>();
  private static particleCache = new Map<string, AuraParticleCache>();

  constructor(config: AuraEffectConfig) {
    this.skill = config.skill;
    this.element = config.skill.element;
    this.position = { x: config.position.x, y: config.position.y };
    this.radius = SkillCreatorV2.getEffectiveRadius(config.skill);
    this.color = this.getElementColor(config.skill.element);
    this.brightColor = this.getBrightColor(config.skill.element);
  }

  /**
   * Update position (follows player) - in-place, no allocation
   */
  updatePosition(position: Vector2): void {
    this.position.x = position.x;
    this.position.y = position.y;
  }

  /**
   * Stop the aura effect
   */
  stop(): void {
    this.isComplete = true;
  }

  update(deltaTime: number): void {
    // Aura continues until explicitly stopped or skill deactivated
    if (!this.skill.isActive) {
      // Fade out before completing
      this.toggleProgress -= deltaTime * this.toggleSpeed;
      if (this.toggleProgress <= 0) {
        this.toggleProgress = 0;
        this.isComplete = true;
      }
    } else {
      // Fade in
      if (this.toggleProgress < 1) {
        this.toggleProgress = Math.min(1, this.toggleProgress + deltaTime * this.toggleSpeed);
      }
    }

    // Update animation timers
    this.pulseTime += deltaTime;
    this.particleAngle += deltaTime * 0.5;
  }

  /** Get or create the cached glow ring offscreen canvas */
  private getGlowCanvas(radius: number, zoom: number): HTMLCanvasElement {
    const key = this.color;
    const cached = AuraEffect.glowCache.get(key);
    // Bucket radius to nearest 10 to reduce cache churn from pulse animation
    const bucketR = Math.round(radius / 10) * 10 || 10;
    if (cached && cached.zoom === zoom && cached.radius === bucketR) {
      return cached.canvas;
    }

    const size = Math.ceil(bucketR * 2) + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, bucketR * 0.7, cx, cx, bucketR);
    grad.addColorStop(0, `${this.color}00`);
    grad.addColorStop(0.5, `${this.color}20`);
    grad.addColorStop(0.8, `${this.color}40`);
    grad.addColorStop(1, `${this.color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, bucketR, 0, Math.PI * 2);
    ctx.fill();

    AuraEffect.glowCache.set(key, { canvas, zoom, radius: bucketR });
    return canvas;
  }

  /** Get or create the cached inner pulse offscreen canvas */
  private getInnerCanvas(zoom: number): HTMLCanvasElement {
    const key = this.color;
    const cached = AuraEffect.innerCache.get(key);
    if (cached && cached.zoom === zoom) {
      return cached.canvas;
    }

    // Use a fixed reference radius for inner pulse (will scale at draw time)
    const refRadius = 64;
    const size = refRadius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, refRadius);
    grad.addColorStop(0, `${this.color}30`);
    grad.addColorStop(0.6, `${this.color}15`);
    grad.addColorStop(1, `${this.color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, refRadius, 0, Math.PI * 2);
    ctx.fill();

    AuraEffect.innerCache.set(key, { canvas, zoom });
    return canvas;
  }

  /** Get or create the cached particle glow offscreen canvas */
  private getParticleCanvas(zoom: number): HTMLCanvasElement {
    const key = this.color;
    const cached = AuraEffect.particleCache.get(key);
    if (cached && cached.zoom === zoom) {
      return cached.canvas;
    }

    const radius = Math.ceil(6 * zoom);
    const size = radius * 2 + 4;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    const cx = size / 2;

    const grad = ctx.createRadialGradient(cx, cx, 0, cx, cx, radius);
    grad.addColorStop(0, this.brightColor);
    grad.addColorStop(0.5, `${this.color}aa`);
    grad.addColorStop(1, `${this.color}00`);

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cx, radius, 0, Math.PI * 2);
    ctx.fill();

    AuraEffect.particleCache.set(key, { canvas, zoom });
    return canvas;
  }

  render(renderer: Renderer, camera: Camera): void {
    if (this.isComplete || this.toggleProgress <= 0) return;

    const screenPos = camera.worldToScreenInPlace(this.position.x, this.position.y);
    const sx = screenPos.x;
    const sy = screenPos.y;
    const ctx = renderer.context;
    const zoom = camera.zoom;

    ctx.save();

    // Apply toggle fade (smooth in/out)
    const t = this.toggleProgress;
    ctx.globalAlpha = t;

    // Scale up during toggle-in for expansion feel
    const toggleScale = 0.5 + t * 0.5;

    // Pulse effect (0.9 to 1.1 scale)
    const pulse = 1 + Math.sin(this.pulseTime * 3) * 0.1;
    const currentRadius = this.radius * zoom * pulse * toggleScale;

    // Isometric ground ellipse indicator (subtle, under everything)
    ctx.save();
    ctx.globalAlpha = t * 0.15;
    ctx.beginPath();
    ctx.ellipse(sx, sy, currentRadius, currentRadius * 0.55, 0, 0, Math.PI * 2);
    ctx.fillStyle = this.color;
    ctx.fill();
    ctx.restore();

    // Isometric ellipse border (dashed, rotating)
    ctx.save();
    ctx.globalAlpha = t * 0.5;
    ctx.beginPath();
    ctx.ellipse(sx, sy, currentRadius * 0.95, currentRadius * 0.95 * 0.55, 0, 0, Math.PI * 2);
    ctx.strokeStyle = `${this.color}80`;
    ctx.lineWidth = 2 * zoom;
    ctx.setLineDash([10 * zoom, 15 * zoom]);
    ctx.lineDashOffset = -this.pulseTime * 30;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Outer glow ring (cached offscreen canvas)
    const glowCanvas = this.getGlowCanvas(currentRadius, zoom);
    const glowHalf = glowCanvas.width / 2;
    ctx.drawImage(glowCanvas, sx - glowHalf, sy - glowHalf);

    // Inner pulse circle (cached offscreen canvas, scaled)
    const innerPulse = 1 + Math.sin(this.pulseTime * 4 + 1) * 0.15;
    const innerRadius = currentRadius * 0.4 * innerPulse;
    const innerCanvas = this.getInnerCanvas(zoom);
    const innerHalf = innerCanvas.width / 2;
    const innerScale = innerRadius / 64; // 64 = reference radius used in cache
    ctx.drawImage(
      innerCanvas,
      sx - innerHalf * innerScale,
      sy - innerHalf * innerScale,
      innerCanvas.width * innerScale,
      innerCanvas.height * innerScale
    );

    // Floating particles around the aura (cached particle glow)
    // Particles follow isometric ellipse path
    const particleCanvas = this.getParticleCanvas(zoom);
    const pHalf = particleCanvas.width / 2;
    const particleCount = 8;
    for (let i = 0; i < particleCount; i++) {
      const angle = this.particleAngle + (i / particleCount) * Math.PI * 2;
      const particleRadiusX = currentRadius * 0.85;
      const particleRadiusY = particleRadiusX * 0.55; // Isometric squash
      const wobble = Math.sin(this.pulseTime * 2 + i * 0.7) * 5 * zoom;

      const px = sx + Math.cos(angle) * (particleRadiusX + wobble);
      const py = sy + Math.sin(angle) * (particleRadiusY + wobble);

      // Stamp cached particle glow
      ctx.drawImage(particleCanvas, px - pHalf, py - pHalf);

      // Bright center
      ctx.beginPath();
      ctx.arc(px, py, 2 * zoom, 0, Math.PI * 2);
      ctx.fillStyle = this.brightColor;
      ctx.fill();
    }

    // Element symbol in center (subtle)
    const symbol = this.getElementSymbol(this.element);
    const symbolAlpha = (0.3 + Math.sin(this.pulseTime * 2) * 0.1) * t;
    ctx.font = `${24 * zoom}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = `${this.color}${Math.floor(symbolAlpha * 255).toString(16).padStart(2, '0')}`;
    ctx.fillText(symbol, sx, sy);

    ctx.restore();
  }

  private getElementColor(element: ElementType): string {
    const colors: Record<string, string> = {
      physical: '#cccccc',
      od: '#cc5544',
      su: '#44aacc',
      gok: '#5588cc',
      karanlik: '#8855aa',
    };
    return colors[element] || '#cccccc';
  }

  private getBrightColor(element: ElementType): string {
    const colors: Record<string, string> = {
      physical: '#ffffff',
      od: '#ffaa88',
      su: '#88eeff',
      gok: '#aaddff',
      karanlik: '#cc88ff',
    };
    return colors[element] || '#ffffff';
  }

  private getElementSymbol(element: ElementType): string {
    const symbols: Record<string, string> = {
      physical: '⚔',
      od: '🔥',
      su: '💧',
      gok: '⚡',
      karanlik: '💀',
    };
    return symbols[element] || '◆';
  }

  /**
   * Check if entity is affected by aura (always true - auras are global)
   */
  isEntityInRange(_entityX: number, _entityY: number): boolean {
    return true; // Auras affect all player damage globally
  }

  /**
   * Get aura info for buff display
   */
  getAuraInfo(): { element: ElementType; skillId: string; skillName: string } {
    return {
      element: this.element,
      skillId: this.skill.id,
      skillName: this.skill.name,
    };
  }

  getEffectStrength(): number {
    return SkillCreatorV2.getEffectStrength(this.skill);
  }

  getElement(): ElementType {
    return this.element;
  }

  getSkillId(): string {
    return this.skill.id;
  }

  getManaReservePercent(): number {
    return this.skill.manaReservePercent || 20;
  }
}

/**
 * Factory function to create aura effect from skill
 */
export function createAuraEffect(config: AuraEffectConfig): AuraEffect {
  return new AuraEffect(config);
}
